import { inject, Injectable } from '@angular/core';
import { type Observable, of } from 'rxjs';

import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { type DatasetRow } from '@blaze-shared/schema-domain';
import { type InsertWorkflowNode } from '@blaze-shared/workflow-domain';

import { NodeResultMessagesService } from '../../node-result-messages.service';

/**
 * Inserts rows built from the node's assignments.
 *
 * A list of input rows inserts one row per input, each evaluated in its own context; a single
 * object inserts one row. Restricted columns are dropped with a message rather than sent — but
 * only on the batch path, which is how the node has always behaved.
 */
@Injectable({ providedIn: 'root' })
export class DatasetInsertActionService {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly iss = inject(IntelliSenseService);
  private readonly nodeResultMessages = inject(NodeResultMessagesService);
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);

  datasetInsert(
    node: InsertWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
    workflowId: string,
    workflowExecutionId: string,
  ): Observable<any> {
    if (!node.datasetId) {
      return of([]);
    }
    const { errorMessage, successMessage } = this.nodeResultMessages.getErrorAndSuccessMessage(
      node,
      nodeInput,
      ctx,
    );

    if (Array.isArray(nodeInput) && nodeInput.length) {
      return this.datasetRowsService.insertBatch({
        datasetId: node.datasetId,
        errorMessage,
        rows: nodeInput.map((row) => this.buildRow(node, this.iss.updateRuntimeContext(ctx, row))),
        showThemedNotification: true,
        successMessage,
        workflowExecutionId,
        workflowId,
      });
    }
    if (Array.isArray(nodeInput) && !nodeInput.length) {
      return of([]);
    }
    return this.datasetRowsService.insert({
      datasetId: node.datasetId,
      errorMessage,
      row: this.buildRowWithoutRestrictionCheck(
        node,
        this.iss.updateRuntimeContext(ctx, nodeInput),
      ),
      showThemedNotification: true,
      successMessage,
      workflowExecutionId,
      workflowId,
    });
  }

  private buildRow(node: InsertWorkflowNode, context: RuntimeContext): DatasetRow {
    const row: DatasetRow = {};
    node.assignments.forEach(({ name, value }) => {
      if (this.schemaService.isColumnRestricted(name, node.datasetId)) {
        const errorMsg = `Unable to update restricted column ${name}. Removing it from the query.`;
        this.notificationService.error(errorMsg, true);
        console.error(errorMsg);
        return;
      }
      row[this.schemaService.getColumnName(name) || ''] = this.iss.eval(value, context);
    });
    return row;
  }

  private buildRowWithoutRestrictionCheck(
    node: InsertWorkflowNode,
    context: RuntimeContext,
  ): DatasetRow {
    const row: DatasetRow = {};
    node.assignments.forEach(({ name, value }) => {
      row[this.schemaService.getColumnName(name) || ''] = this.iss.eval(value, context);
    });
    return row;
  }
}
